import { ExternalLink, Receipt } from "lucide-react";
import { useGetList, type Identifier } from "ra-core";

import { Badge } from "@/components/ui/badge";

import {
  PAYMENT_STATUSES,
  SUBSCRIPTION_KINDS,
  SUBSCRIPTION_STATUSES,
  formatEuro,
  formatLongDate,
  labelOf,
} from "./gulaConstants";
import type { GulaPayment, GulaSubscription } from "./gulaTypes";

const INTERVAL_LABEL: Record<string, string> = {
  month: "mensual",
  year: "anual",
};

const statusVariant = (status: string) => {
  const tone = SUBSCRIPTION_STATUSES.find(
    (option) => option.value === status,
  )?.tone;
  if (tone === "ok") return "default" as const;
  if (tone === "warn") return "destructive" as const;
  return "secondary" as const;
};

const SubscriptionLine = ({
  subscription,
}: {
  subscription: GulaSubscription;
}) => {
  const isLive = ["active", "trialing", "past_due"].includes(
    subscription.status,
  );
  return (
    <div className="rounded-md border p-3 text-sm">
      <div className="flex items-center justify-between gap-2">
        <strong>
          <span className="mr-1 font-normal text-muted-foreground">
            {labelOf(SUBSCRIPTION_KINDS, subscription.kind)}:
          </span>
          {subscription.plan_name ?? "Mantenimiento Gula"}
          {subscription.billing_interval
            ? ` (${INTERVAL_LABEL[subscription.billing_interval] ?? subscription.billing_interval})`
            : ""}
        </strong>
        <Badge variant={statusVariant(subscription.status)}>
          {labelOf(SUBSCRIPTION_STATUSES, subscription.status)}
        </Badge>
      </div>
      <p className="text-muted-foreground">
        {formatEuro(subscription.amount)}
        {subscription.billing_interval === "year" ? "/año" : "/mes"}
        {subscription.started_at
          ? ` · desde ${formatLongDate(subscription.started_at)}`
          : ""}
      </p>
      {isLive && subscription.cancel_at_period_end ? (
        <p className="text-destructive">
          Se cancela el {formatLongDate(subscription.current_period_end)}
        </p>
      ) : isLive && subscription.current_period_end ? (
        <p className="text-muted-foreground">
          Próximo cobro: {formatLongDate(subscription.current_period_end)}
        </p>
      ) : subscription.canceled_at ? (
        <p className="text-muted-foreground">
          Cancelado el {formatLongDate(subscription.canceled_at)}
        </p>
      ) : null}
    </div>
  );
};

/** Maintenance plan and Stripe payments of one client. */
export const ClientStripe = ({
  contactId,
  stripeCustomerId,
}: {
  contactId: Identifier;
  stripeCustomerId?: string | null;
}) => {
  const { data: subscriptions = [] } = useGetList<GulaSubscription>(
    "gula_subscriptions",
    {
      filter: { contact_id: contactId },
      pagination: { page: 1, perPage: 20 },
      sort: { field: "created_at", order: "DESC" },
    },
  );
  const { data: payments = [] } = useGetList<GulaPayment>("gula_payments", {
    filter: { contact_id: contactId },
    pagination: { page: 1, perPage: 100 },
    sort: { field: "paid_at", order: "DESC" },
  });
  const totalPaid = payments
    .filter((payment) => payment.status === "paid")
    .reduce((sum, payment) => sum + Number(payment.amount), 0);

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">
          Stripe: suscripciones y cobros
        </h4>
        {stripeCustomerId ? (
          <a
            href={`https://dashboard.stripe.com/customers/${stripeCustomerId}`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 text-xs text-muted-foreground hover:underline"
          >
            Ver en Stripe
            <ExternalLink className="h-3 w-3" />
          </a>
        ) : null}
      </div>

      {subscriptions.length ? (
        subscriptions.map((subscription) => (
          <SubscriptionLine key={subscription.id} subscription={subscription} />
        ))
      ) : (
        <p className="text-sm text-muted-foreground">
          Sin suscripciones en Stripe.
        </p>
      )}

      {payments.length ? (
        <div className="rounded-md border text-sm">
          {payments.map((payment) => (
            <div
              key={payment.id}
              className="flex items-center justify-between gap-2 border-b px-3 py-2 last:border-b-0"
            >
              <div className="flex min-w-0 flex-col">
                <span className="truncate">
                  {payment.description ?? "Pago"}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatLongDate(payment.paid_at)}
                  {payment.status !== "paid"
                    ? ` · ${labelOf(PAYMENT_STATUSES, payment.status)}`
                    : ""}
                </span>
              </div>
              <div className="flex items-center gap-2 whitespace-nowrap">
                <span
                  className={
                    payment.status === "refunded"
                      ? "text-muted-foreground line-through"
                      : "font-medium"
                  }
                >
                  {formatEuro(Number(payment.amount))}
                </span>
                {payment.receipt_url ? (
                  <a
                    href={payment.receipt_url}
                    target="_blank"
                    rel="noreferrer"
                    title="Recibo"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <Receipt className="h-4 w-4" />
                  </a>
                ) : null}
              </div>
            </div>
          ))}
          <div className="flex justify-between bg-muted/50 px-3 py-2 font-medium">
            <span>Total cobrado</span>
            <span>{formatEuro(totalPaid)}</span>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Sin cobros en Stripe.</p>
      )}
    </section>
  );
};
